// @ts-nocheck
class ProductSpotlight extends HTMLElement {
  connectedCallback() {
    this._moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this._bundleDiscountRules = this._parseBundleDiscountRules(this.dataset.bundleDiscountRules || '[]');
    this._singleQty = 1;
    this._singleUnitPriceCents = this._readMoneyFromElement('[data-ps-price]');
    this._singleComparePriceCents = 0;
    this._singleSaveCents = 0;
    this._isVarietyMode = false;
    this._bundleTotalCents = 0;
    this._originalTitle = this.querySelector('[data-ps-title]')?.textContent || '';
    this._bundleVarietyImages = this._resolveBundleVarietyImages();
    this._singleGalleryState = this._readInitialGalleryState();

    const initialCompare = this._readMoneyFromElement('[data-ps-compare-price]');
    if (initialCompare > this._singleUnitPriceCents) {
      this._singleComparePriceCents = initialCompare;
      this._singleSaveCents = initialCompare - this._singleUnitPriceCents;
    }

    this._initSwiper();
    this._initFlavorSwiper();
    this._initDesktopGallery();
    this._initPurchaseOptions();
    this._initDeliverySelect();
    this._initAccordions();
    this._initQuantity();
    this._initPackSelector();
    this._initFlavorSelector();
    this._initAddToCart();
    this._initBundlePricingBridge();

    this._emitPackModeState();
    this._emitSingleState();
  }

  disconnectedCallback() {
    if (this._swiper) this._swiper.destroy(true, true);
    if (this._flavorSwiper) this._flavorSwiper.destroy(true, true);
    if (this._deliveryOutsideHandler) {
      document.removeEventListener('click', this._deliveryOutsideHandler);
    }
    if (this._onBundleStateChange) {
      document.removeEventListener('pdp:bundle-state-change', this._onBundleStateChange);
    }
  }

  /* ── Swiper (mobile) ──────────────────────────────────────── */
  _initSwiper() {
    const swiperEl = /** @type {HTMLElement | null} */ (this.querySelector('.product-spotlight__swiper'));
    if (!swiperEl || typeof Swiper === 'undefined') return;

    this._swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      loop: false,
      pagination: {
        el: this.querySelector('.product-spotlight__swiper-pagination'),
        clickable: true,
      },
    });
  }

  /* ── Flavor slider ────────────────────────────────────────── */
  _initFlavorSwiper() {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('.product-spotlight__flavor-swiper'));
    if (!el || typeof Swiper === 'undefined') return;
    const prevBtn = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-flavor-prev]'));
    const nextBtn = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-flavor-next]'));

    this._flavorSwiper = new Swiper(el, {
      slidesPerView: 3.4,
      spaceBetween: 4,
      watchOverflow: true,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
        disabledClass: 'swiper-button-disabled',
      },
      breakpoints: {
        769: { slidesPerView: 4, spaceBetween: 8 },
        spaceBetween: 8,
      },
    });
  }

  _applyFlavorSlideOrder(handles) {
    if (!Array.isArray(handles) || !handles.length) return;
    const wrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__flavor-options')
    );
    if (!wrapper) return;

    const slides = /** @type {HTMLElement[]} */ (
      Array.from(wrapper.querySelectorAll('.product-spotlight__flavor-option'))
    );
    if (!slides.length) return;

    const slidesByHandle = new Map();
    slides.forEach(slide => {
      const handle = slide.dataset.flavor;
      if (handle) slidesByHandle.set(handle, slide);
    });

    const allowed = new Set(handles);
    slides.forEach(slide => {
      const handle = slide.dataset.flavor;
      slide.style.display = handle && allowed.has(handle) ? '' : 'none';
    });

    handles.forEach(handle => {
      const slide = slidesByHandle.get(handle);
      if (slide) wrapper.appendChild(slide);
    });

    if (this._flavorSwiper && typeof this._flavorSwiper.update === 'function') {
      this._flavorSwiper.update();
    }
  }

  /* ── Desktop gallery (thumb click -> main hero) ─────────── */
  _initDesktopGallery() {
    const thumbnails = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-thumbnails]'));
    const heroImg = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-ps-hero-img]'));
    if (!thumbnails || !heroImg) return;

    thumbnails.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
      const thumb = /** @type {HTMLElement | null} */ (
        target ? target.closest('.product-spotlight__thumb') : null
      );
      if (!thumb || !thumbnails.contains(thumb)) return;

      const thumbs = Array.from(thumbnails.querySelectorAll('.product-spotlight__thumb'));
      const thumbIndex = thumbs.indexOf(thumb);
      if (thumbIndex < 0) return;

      this._promoteDesktopThumbToHero(thumbIndex);
    });
  }

  _promoteDesktopThumbToHero(thumbIndex) {
    if (this._isVarietyMode && Array.isArray(this._bundleVarietyImages) && this._bundleVarietyImages.length > 1) {
      const targetIndex = thumbIndex + 1;
      if (targetIndex <= 0 || targetIndex >= this._bundleVarietyImages.length) return;

      const reordered = [...this._bundleVarietyImages];
      const [selected] = reordered.splice(targetIndex, 1);
      if (!selected) return;
      reordered.unshift(selected);
      this._bundleVarietyImages = reordered;
      this._applyBundleVarietyGallery(true);
      return;
    }

    const state = this._singleGalleryState;
    if (!state || !Array.isArray(state.images) || state.images.length <= 1) return;

    const targetIndex = thumbIndex + 1;
    if (targetIndex <= 0 || targetIndex >= state.images.length) return;

    const reordered = [...state.images];
    const [selected] = reordered.splice(targetIndex, 1);
    if (!selected) return;
    reordered.unshift(selected);

    state.images = reordered;
    state.thumbImages = reordered.slice(1, 5);
    this._singleGalleryState = state;
    this._applySingleGalleryState(true);
  }

  /* ── Purchase type toggle ─────────────────────────────────── */
  _initPurchaseOptions() {
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('.product-spotlight__radio'));
    const options = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.product-spotlight__purchase-option'));
    if (!radios.length) return;

    /** @param {string} value */
    const updateState = (value) => {
      options.forEach(option => {
        option.classList.toggle(
          'product-spotlight__purchase-option--active',
          option.dataset.purchaseType === value
        );
      });
    };

    radios.forEach(radio => {
      radio.addEventListener('change', () => updateState(radio.value));
    });

    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('.product-spotlight__radio:checked'));
    if (checked) updateState(checked.value);
  }

  _formatMoney(cents) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
    const amount = (cents / 100).toFixed(2);
    return this._moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
  }

  _setSubscribePricing(plan) {
    const saveEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-subscribe-save]'));
    const compareEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-subscribe-compare-price]'));
    const priceEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-subscribe-price]'));
    if (!saveEl || !compareEl || !priceEl) return;

    // Keep subscribe pricing in lockstep with pack selector quantity.
    // In pack-qty mode, selling-plan prices come per unit and must be
    // multiplied by the active pack quantity, same as one-time pricing.
    const qty = !this._isVarietyMode && this._singleQty > 0 ? this._singleQty : 1;
    const activePackQtyButton = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__pack-option--active[data-pack-qty]')
    );
    const bundleDiscountPct = activePackQtyButton ? this._getBundleDiscountForQty(qty) : 0;

    if (plan && typeof plan.price === 'number') {
      const comparePrice = typeof plan.comparePrice === 'number' ? plan.comparePrice : 0;
      const baseCompareUnit = comparePrice > 0 ? comparePrice : plan.price;
      const bundleDiscountedUnit = bundleDiscountPct > 0
        ? Math.round(baseCompareUnit * (100 - bundleDiscountPct) / 100)
        : baseCompareUnit;

      // Match bundle rounding strategy: apply subscribe discount as floor
      // over already tier-discounted unit cents.
      let subscriptionDiscountPct = parseInt(plan.discountPct || '0', 10);
      if (!(subscriptionDiscountPct > 0) && baseCompareUnit > 0 && plan.price > 0 && plan.price < baseCompareUnit) {
        subscriptionDiscountPct = Math.floor((baseCompareUnit - plan.price) * 100 / baseCompareUnit);
      }
      if (!(subscriptionDiscountPct > 0)) subscriptionDiscountPct = 0;

      const subscriptionSavingsUnit = subscriptionDiscountPct > 0
        ? Math.floor(Math.max(bundleDiscountedUnit, 0) * subscriptionDiscountPct / 100)
        : 0;

      const subscribeUnitPrice = Math.max(bundleDiscountedUnit - subscriptionSavingsUnit, 0);
      const subscribeTotalPrice = subscribeUnitPrice * qty;
      const comparePriceTotal = baseCompareUnit * qty;
      const saveCents = Math.max(comparePriceTotal - subscribeTotalPrice, 0);

      priceEl.textContent = this._formatMoney(subscribeTotalPrice);

      if (comparePriceTotal > subscribeTotalPrice) {
        compareEl.textContent = this._formatMoney(comparePriceTotal);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }

      if (saveCents > 0) {
        saveEl.textContent = `Save ${this._formatMoney(saveCents)}`;
        saveEl.style.display = '';
      } else {
        saveEl.style.display = 'none';
      }
      return;
    }

    saveEl.style.display = 'none';
    compareEl.style.display = 'none';
  }

  _syncSubscribePricingFromSelectedPlan() {
    const selectedOption = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__delivery-option--selected')
      || this.querySelector('.product-spotlight__delivery-option')
    );

    if (!selectedOption) {
      this._setSubscribePricing(null);
      return;
    }

    this._setSubscribePricing({
      price: parseInt(selectedOption.dataset.sellingPlanPrice || '0', 10),
      comparePrice: parseInt(selectedOption.dataset.sellingPlanComparePrice || '0', 10),
      discountPct: parseInt(selectedOption.dataset.sellingPlanDiscountPct || '0', 10),
    });
  }

  /* ── Delivery frequency select ────────────────────────────── */
  _initDeliverySelect() {
    const selects = this.querySelectorAll('[data-delivery-select]');
    selects.forEach(select => {
      const trigger = /** @type {HTMLButtonElement | null} */ (select.querySelector('.product-spotlight__delivery-row'));
      const list = /** @type {HTMLElement | null} */ (select.querySelector('.product-spotlight__delivery-options'));
      const label = /** @type {HTMLElement | null} */ (select.querySelector('.product-spotlight__delivery-text'));
      if (!trigger || !list || !label) return;

      const open = () => {
        list.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        select.classList.add('product-spotlight__delivery-select--open');
      };

      const close = () => {
        list.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        select.classList.remove('product-spotlight__delivery-select--open');
      };

      list.hidden = true;

      trigger.addEventListener('click', () => {
        trigger.getAttribute('aria-expanded') === 'true' ? close() : open();
      });

      list.addEventListener('click', (event) => {
        const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
        const option = /** @type {HTMLElement | null} */ (
          target ? target.closest('.product-spotlight__delivery-option') : null
        );
        if (!option) return;

        list.querySelectorAll('.product-spotlight__delivery-option').forEach(o => {
          o.classList.remove('product-spotlight__delivery-option--selected');
          o.setAttribute('aria-selected', 'false');
        });

        option.classList.add('product-spotlight__delivery-option--selected');
        option.setAttribute('aria-selected', 'true');
        label.textContent = option.textContent || '';

        const selectedPlanId = option.dataset.sellingPlanId || '';
        if (selectedPlanId) {
          trigger.dataset.psSelectedSellingPlanId = selectedPlanId;
          this._setSubscribePricing({
            price: parseInt(option.dataset.sellingPlanPrice || '0', 10),
            comparePrice: parseInt(option.dataset.sellingPlanComparePrice || '0', 10),
            discountPct: parseInt(option.dataset.sellingPlanDiscountPct || '0', 10),
          });
        }

        close();
      });

      if (!this._deliveryOutsideHandler) {
        this._deliveryOutsideHandler = /** @param {Event} e */ (e) => {
          const target = /** @type {HTMLElement} */ (e.target);
          if (!select.contains(target)) close();
        };
        document.addEventListener('click', this._deliveryOutsideHandler);
      }
    });
  }

  /* ── Accordions ───────────────────────────────────────────── */
  _initAccordions() {
    const accordions = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.product-spotlight__accordion'));

    this.querySelectorAll('.product-spotlight__accordion-header').forEach(header => {
      header.addEventListener('click', () => {
        const accordion = header.closest('.product-spotlight__accordion');
        if (!accordion) return;

        const isOpen = accordion.classList.contains('product-spotlight__accordion--open');

        accordions.forEach(item => {
          item.classList.remove('product-spotlight__accordion--open');
          item
            .querySelector('.product-spotlight__accordion-header')
            ?.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          accordion.classList.add('product-spotlight__accordion--open');
          header.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ── Quantity stepper ─────────────────────────────────────── */
  _initQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('.product-spotlight__qty-input'));
    if (!input) return;

    const emitQty = () => {
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);
      const raw = parseInt(input.value, 10);
      const qty = Number.isNaN(raw) ? min : Math.min(Math.max(raw, min), max);
      input.value = String(qty);
      this._singleQty = qty;
      this._emitSingleState();
    };

    this.querySelector('.product-spotlight__qty-btn--minus')?.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      const min = parseInt(input.min || '1', 10);
      if (val > min) {
        input.value = String(val - 1);
        emitQty();
      }
    });

    this.querySelector('.product-spotlight__qty-btn--plus')?.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      const max = parseInt(input.max || '99', 10);
      if (val < max) {
        input.value = String(val + 1);
        emitQty();
      }
    });

    const paramQty = parseInt(new URLSearchParams(window.location.search).get('quantity') || '', 10);
    if (!Number.isNaN(paramQty)) {
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);
      input.value = String(Math.min(Math.max(paramQty, min), max));
    }

    input.addEventListener('input', emitQty);
    input.addEventListener('change', emitQty);
    emitQty();
  }

  _syncQuantityMinimum(minQty, preferredQty = null) {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('.product-spotlight__qty-input'));
    if (!input) return 0;

    const resolvedMin = Number.isInteger(minQty) && minQty > 0 ? minQty : parseInt(input.min || '1', 10) || 1;
    const currentValue = parseInt(input.value, 10) || resolvedMin;
    const resolvedPreferredQty = Number.isInteger(preferredQty) && preferredQty > 0 ? preferredQty : currentValue;
    const resolvedQty = Math.max(resolvedPreferredQty, resolvedMin);

    input.min = String(resolvedMin);
    input.value = String(resolvedQty);
    this._singleQty = resolvedQty;

    return resolvedQty;
  }

  /* ── Pack selector (per-variant) ──────────────────────────── */
  _initPackSelector() {
    const container = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__pack-options')
    );
    if (!container) return;

    container.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = /** @type {HTMLElement | null} */ (
        target ? target.closest('[data-ps-pack-variant]') : null
      );
      if (!button || !container.contains(button)) return;
      this._activatePackVariantButton(button);
    });

    // Initialize state for the active pack-qty button on page load.
    const activePackQtyBtn = /** @type {HTMLElement | null} */ (
      container.querySelector('.product-spotlight__pack-option--active[data-pack-qty]')
    );
    if (activePackQtyBtn) this._syncPackButtonState(activePackQtyBtn);
  }

  _readPackButtonData(button) {
    const dataScript = button.querySelector('.ps-variant-data');
    let data = {};
    if (dataScript) {
      try {
        data = JSON.parse(dataScript.textContent || '{}');
      } catch (e) {
        console.error('[ProductSpotlight] Error parsing variant data', e);
      }
    }
    return data;
  }

  _applyPackVariantData(button, data) {
    const packQty = parseInt(button.dataset.packQty || '0', 10);
    if (packQty > 0) {
      const baseUnitCents = parseInt(button.dataset.baseUnitCents || '0', 10) || 0;
      const perCanCents = parseInt(button.dataset.perCanCents || '0', 10) || 0;
      this._singleUnitPriceCents = perCanCents;
      this._singleComparePriceCents = baseUnitCents;
      this._singleSaveCents = Math.max(baseUnitCents - perCanCents, 0);
      this.dataset.singleUnitPriceCents = String(perCanCents);

      const qtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.product-spotlight__qty-input'));
      if (qtyInput) this._syncQuantityMinimum(parseInt(data.bundleMinQty || '0', 10), packQty);

      this._renderPriceRow();

      const variantId = button.dataset.variantId;
      const addToCartBtn = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-add-to-cart]'));
      if (addToCartBtn && variantId) addToCartBtn.dataset.variantId = String(variantId);

      this._updatePurchasePack(button);
      return;
    }

    this._singleUnitPriceCents = parseInt(data.price || button.dataset.price || '0', 10) || 0;
    this._singleComparePriceCents = parseInt(data.comparePrice || button.dataset.comparePrice || '0', 10) || 0;
    this._singleSaveCents = parseInt(data.saveCents || button.dataset.saveCents || '0', 10) || 0;
    this.dataset.singleUnitPriceCents = String(this._singleUnitPriceCents);

    this._renderPriceRow();

    const variantId = data.variantId || button.dataset.variantId;
    const addToCartBtn = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-add-to-cart]'));
    if (addToCartBtn && variantId) {
      addToCartBtn.dataset.variantId = String(variantId);
    }

    this._updateSubscribeOptions({ sellingPlans: Array.isArray(data.sellingPlans) ? data.sellingPlans : [] });

    this._updatePurchasePack(button);
  }

  _updatePurchasePack(button) {
    const labels = this.querySelectorAll('[data-ps-purchase-pack]');
    if (!labels.length) return;

    const optionLabel = button.dataset.pack
      ? String(button.dataset.pack).split('-')[0]
      : '';
    const optionNameUpper = button.dataset.pack
      ? String(button.dataset.pack).split('-').slice(1).join('-')
      : '';
    const text = optionLabel && optionNameUpper
      ? `${optionLabel} ${optionNameUpper.toLowerCase()}`
      : '';

    labels.forEach(el => {
      el.textContent = text;
    });
  }

  _parseBundleDiscountRules(value) {
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((rule) => {
          const minimum = parseInt(rule?.minimum || '0', 10);
          const rawMaximum = rule?.maximum;
          const maximum = rawMaximum === null || rawMaximum === undefined || rawMaximum === ''
            ? null
            : parseInt(rawMaximum, 10);
          const discountValue = Number(rule?.value || 0);
          const type = String(rule?.type || '').toLowerCase().trim();

          if (!Number.isInteger(minimum) || minimum <= 0 || !(discountValue > 0)) return null;

          return {
            minimum,
            maximum: Number.isInteger(maximum) && maximum > 0 ? maximum : null,
            value: discountValue,
            type,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.minimum - b.minimum);
    } catch (error) {
      console.error('[ProductSpotlight] Invalid bundle discount rules payload', error);
      return [];
    }
  }

  _getBundleDiscountForQty(qty) {
    const safeQty = Number.isInteger(qty) && qty > 0 ? qty : 0;
    const rules = Array.isArray(this._bundleDiscountRules) ? this._bundleDiscountRules : [];

    if (safeQty > 0 && rules.length > 0) {
      const matches = rules.filter((rule) => {
        const type = rule.type;
        const minimum = rule.minimum;
        const maximum = rule.maximum;

        if (type === 'fixed quantity' || type === 'fixed_quantity' || type === 'fixed') {
          return safeQty === minimum;
        }

        if (Number.isInteger(maximum) && maximum > 0) {
          return safeQty >= minimum && safeQty <= maximum;
        }

        return safeQty >= minimum;
      });

      if (matches.length > 0) {
        const winner = matches.reduce((best, current) => {
          if (!best) return current;
          return current.minimum >= best.minimum ? current : best;
        }, null);
        return winner && winner.value > 0 ? Math.round(winner.value) : 0;
      }
    }

    return 0;
  }

  _activatePackVariantButton(button) {
    if (button.classList.contains('product-spotlight__pack-option--active')) return;

    const container = button.closest('.product-spotlight__pack-options');
    if (container) {
      container.querySelectorAll('.product-spotlight__pack-option--active').forEach(b => {
        b.classList.remove('product-spotlight__pack-option--active');
      });
    }
    button.classList.add('product-spotlight__pack-option--active');

    const data = this._readPackButtonData(button);
    this._applyPackVariantData(button, data);

    const targetUrl = button.dataset.url;
    if (targetUrl) {
      try {
        const nextUrl = new URL(targetUrl, window.location.origin);
        nextUrl.hash = window.location.hash;
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const target = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        if (current !== target) {
          window.history.replaceState(window.history.state, '', target);
        }
      } catch (error) {
        console.warn('[ProductSpotlight] Unable to update URL for variant', error);
      }
    }

    this._emitSingleState();
  }

  _syncPackButtonState(button) {
    const data = this._readPackButtonData(button);
    this._applyPackVariantData(button, data);
    this._emitSingleState();
  }

  _escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  _rebuildPackVariants(packVariants, activeVariantId) {
    const container = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__pack-options')
    );
    const selector = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__pack-selector')
    );

    if (!Array.isArray(packVariants) || packVariants.length <= 1) {
      selector.style.display = 'none';
      return;
    }
    selector.style.display = '';

    const esc = (v) => this._escapeHtml(v);
    container.innerHTML = packVariants.map((v) => {
      const isActive = String(v.variantId) === String(activeVariantId);
      const json = JSON.stringify({
        variantId: v.variantId,
        price: v.price,
        comparePrice: v.comparePrice,
        saveCents: v.saveCents,
        sellingPlans: Array.isArray(v.sellingPlans) ? v.sellingPlans : [],
      }).replace(/</g, '\\u003C');
      const label = `${esc(v.optionLabel)}-${esc(v.optionNameUpper)}`;
      const priceLine = `${esc(v.perCanMoney || v.priceMoney)}/can`;
      return (
        `<button class="product-spotlight__pack-option${isActive ? ' product-spotlight__pack-option--active' : ''}"` +
        ` type="button" data-ps-pack-variant` +
        ` data-variant-id="${esc(v.variantId)}"` +
        ` data-pack="${label}"` +
        ` data-price="${esc(v.price)}"` +
        ` data-compare-price="${esc(v.comparePrice)}"` +
        ` data-save-cents="${esc(v.saveCents)}"` +
        ` data-url="${esc(v.url)}"` +
        ` data-aim>` +
        `<span style="display:block">${label}</span>` +
        `<span style="display:block">${priceLine}</span>` +
        `<script type="application/json" class="ps-variant-data">${json}<\/script>` +
        `</button>`
      );
    }).join('');

    const activeButton = /** @type {HTMLElement | null} */ (
      container.querySelector('.product-spotlight__pack-option--active') ||
      container.querySelector('[data-ps-pack-variant]')
    );
    if (activeButton) {
      activeButton.classList.add('product-spotlight__pack-option--active');
      this._syncPackButtonState(activeButton);
    }
  }

  /**
   * Validate and apply 5-pack variety availability.
   * @param {boolean|null} forcedAvailability
   * @returns {boolean}
   */
  _syncVarietyAvailability(forcedAvailability = null) {
    const packItems = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.product-spotlight__pack-option'));
    const varietyButton = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__pack-option[data-pack*="VARIETY"]')
    );
    const varietyPanel = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-variety-only]'));

    const hasVarietyFromDOM = Boolean(varietyButton && varietyPanel);
    const hasVariety = forcedAvailability === null ? hasVarietyFromDOM : forcedAvailability && hasVarietyFromDOM;

    if (varietyButton) {
      varietyButton.style.display = hasVariety ? '' : 'none';
      if (!hasVariety) {
        varietyButton.classList.remove('product-spotlight__pack-option--active');
      }
    }

    this.querySelectorAll('[data-ps-variety-only]').forEach(el => {
      /** @type {HTMLElement} */ (el).style.display = hasVariety ? '' : 'none';
    });

    const activeItem = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__pack-option.product-spotlight__pack-option--active')
    );

    if (!activeItem || ((activeItem.dataset.pack || '').includes('VARIETY') && !hasVariety)) {
      packItems.forEach(i => i.classList.remove('product-spotlight__pack-option--active'));
      const firstVisible = Array.from(packItems).find(i => /** @type {HTMLElement} */ (i).style.display !== 'none');
      if (firstVisible) firstVisible.classList.add('product-spotlight__pack-option--active');
    }

    const currentActive = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__pack-option.product-spotlight__pack-option--active')
    );
    this._switchProductPanel((currentActive?.dataset.pack) || '');

    return hasVariety;
  }

  /**
   * Toggle single-pack panels vs. variety (bundle) panel
   * @param {string} pack — e.g. '5-PACK' or '5-PACK VARIETY'
   */
  _switchProductPanel(pack) {
    const isVariety = pack.includes('VARIETY');
    this._isVarietyMode = isVariety;
    this.querySelectorAll('[data-ps-single-only]').forEach(el => {
      /** @type {HTMLElement} */ (el).style.display = isVariety ? 'none' : '';
    });
    this.querySelectorAll('[data-ps-variety-only]').forEach(el => {
      /** @type {HTMLElement} */ (el).style.display = isVariety ? '' : 'none';
    });

    const varietyTitle = this.dataset.bundleVarietyTitle;
    const singleTitle = this._singleGalleryState?.title || this._originalTitle;
    this.querySelectorAll('[data-ps-title]').forEach(el => {
      el.textContent = isVariety && varietyTitle ? varietyTitle : singleTitle;
    });

    if (isVariety) {
      this._applyBundleVarietyGallery();
    } else {
      this._applySingleGalleryState();
    }

    this._emitPackModeState();
    this._renderPriceRow();
  }

  /* ── Flavor selector ──────────────────────────────────────── */
  _initFlavorSelector() {
    const flavorOptions = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.product-spotlight__flavor-option'));
    if (!flavorOptions.length) return;

    const applyFlavorOption = (option) => {
      flavorOptions.forEach(o => o.classList.remove('product-spotlight__flavor-option--active'));
      option.classList.add('product-spotlight__flavor-option--active');

      const dataScript = option.querySelector('.ps-flavor-data');
      if (!dataScript) return;

      try {
        const data = JSON.parse(dataScript.textContent || '{}');
        data.flavorGridImage = option.dataset.bvGridImage || '';
        this._updateURLForFlavor(data);
        this._updateFlavorContent(data);
        this._updateBundleBuilder(data);
        this._syncVarietyAvailability(
          typeof data.hasVariety === 'boolean' ? data.hasVariety : null
        );
        this._applyFlavorSlideOrder(data.flavorSlideHandles);
      } catch (e) {
        console.error('[ProductSpotlight] Error parsing flavor data', e);
      }
    };

    flavorOptions.forEach(option => {
      option.addEventListener('click', () => {
        applyFlavorOption(option);
      });
    });

    const initiallyActive = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__flavor-option--active')
    ) || flavorOptions[0];

    if (initiallyActive) {
      applyFlavorOption(initiallyActive);
    }
  }

  _updateURLForFlavor(data) {
    if (!data || typeof data.url !== 'string' || !data.url) return;

    try {
      const nextUrl = new URL(data.url, window.location.origin);
      nextUrl.search = window.location.search;
      nextUrl.hash = window.location.hash;

      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const target = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

      if (current !== target) {
        window.history.replaceState(window.history.state, '', target);
      }
    } catch (error) {
      console.warn('[ProductSpotlight] Unable to update URL for flavor', error);
    }
  }

  /**
   * Update all dynamic PDP content when a flavor is selected
   * @param {Object} data — flavor data object with all product info
   */
  _updateFlavorContent(data) {
    const fmt = (cents) => this._formatMoney(cents);

    // ─── Title ───────────────────────────────────────────────
    this.querySelectorAll('[data-ps-title]').forEach(el => {
      el.textContent = data.title || '';
    });

    // Keep sticky title in lockstep with the active flavor, even if other listeners fail.
    document.querySelectorAll('.pdp-sticky-cart__title').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.textContent = data.title || '';
      }
    });

    this._emitActiveProductChange(data);

    // ─── Description ─────────────────────────────────────────
    this.querySelectorAll('[data-ps-description]').forEach(el => {
      el.innerHTML = data.description || '';
    });

    // ─── Price ───────────────────────────────────────────────
    const priceStr = fmt(data.price);
    this._singleUnitPriceCents = parseInt(data.price || '0', 10) || 0;
    this._singleComparePriceCents = parseInt(data.comparePrice || '0', 10) || 0;
    this._singleSaveCents = parseInt(data.saveCents || '0', 10) || 0;
    this.dataset.singleUnitPriceCents = String(this._singleUnitPriceCents);
    this.querySelectorAll('[data-ps-price]').forEach(el => {
      el.textContent = priceStr;
    });
    this.querySelectorAll('[data-ps-purchase-price]').forEach(el => {
      el.textContent = priceStr;
    });

    // ─── Compare Price ───────────────────────────────────────
    const compareStr = fmt(data.comparePrice);
    this.querySelectorAll('[data-ps-compare-price]').forEach(el => {
      const visible = data.comparePrice > data.price;
      /** @type {HTMLElement} */ (el).style.display = visible ? '' : 'none';
      if (visible) el.textContent = compareStr;
    });

    // ─── Save Badge ──────────────────────────────────────────
    this.querySelectorAll('[data-ps-save-badge]').forEach(el => {
      if (data.saveCents > 0) {
        el.textContent = 'Save ' + fmt(data.saveCents);
        /** @type {HTMLElement} */ (el).style.display = '';
      } else {
        /** @type {HTMLElement} */ (el).style.display = 'none';
      }
    });

    // ─── Reviews ─────────────────────────────────────────────
    const ratingValue = Number(data.rating);
    this.querySelectorAll('[data-ps-stars-fallback]').forEach(el => {
      const ratingPercent = Number.isFinite(ratingValue) && ratingValue > 0
        ? Math.max(0, Math.min(100, ratingValue * 20))
        : 0;
      /** @type {HTMLElement} */ (el).style.setProperty('--ps-rating-percent', `${ratingPercent}%`);
    });

    this.querySelectorAll('[data-ps-rating]').forEach(el => {
      if (Number.isFinite(ratingValue) && ratingValue > 0) {
        el.textContent = ratingValue.toFixed(1);
        /** @type {HTMLElement} */ (el).style.display = '';
      } else {
        el.textContent = '';
        /** @type {HTMLElement} */ (el).style.display = 'none';
      }
    });

    const reviewCountValue = Number(data.reviewCount);
    this.querySelectorAll('[data-ps-review-count]').forEach(el => {
      if (Number.isFinite(reviewCountValue) && reviewCountValue > 0) {
        el.textContent = `[${reviewCountValue.toLocaleString('en-US')}]`;
        /** @type {HTMLElement} */ (el).style.display = '';
      } else {
        el.textContent = '';
        /** @type {HTMLElement} */ (el).style.display = 'none';
      }
    });

    // ─── Reviews block visibility ─────────────────────────────
    const hasReviews = (Number.isFinite(ratingValue) && ratingValue > 0) ||
                       (Number.isFinite(reviewCountValue) && reviewCountValue > 0);
    this.querySelectorAll('[data-ps-reviews-block]').forEach(el => {
      /** @type {HTMLElement} */ (el).style.display = hasReviews ? '' : 'none';
    });

    // ─── Add to Cart Button Variant ID ───────────────────────
    const addToCartBtn = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-add-to-cart]'));
    if (addToCartBtn && data.variantId) {
      addToCartBtn.dataset.variantId = String(data.variantId);
    }

    // ─── Subscribe selling plans ─────────────────────────────
    this._updateSubscribeOptions(data);

    // ─── Pack variants (rebuild from flavor product's variants) ──
    // Pack-qty mode: pack sizes come from the product metafield, buttons already exist —
    // just update each button's variant ID and recalculate per-can price.
    const isPackQtyMode = !!this.querySelector('[data-ps-pack-variant][data-pack-qty]');
    if (isPackQtyMode) {
      const newBasePrice = parseInt(String(data.price || '0'), 10) || 0;
      const newVariantId = String(data.variantId || '');
      this.querySelectorAll('[data-ps-pack-variant][data-pack-qty]').forEach((btn) => {
        const el = /** @type {HTMLElement} */ (btn);
        const packQty = parseInt(el.dataset.packQty || '0', 10);
        const discount = this._getBundleDiscountForQty(packQty);
        const perCanCents = Math.round(newBasePrice * (100 - discount) / 100);
        el.dataset.variantId = newVariantId;
        el.dataset.baseUnitCents = String(newBasePrice);
        el.dataset.perCanCents = String(perCanCents);
        const spans = el.querySelectorAll('span');
        if (spans[1]) spans[1].textContent = `${this._formatMoney(perCanCents)}/can`;
      });
      const activePack = /** @type {HTMLElement | null} */ (
        this.querySelector('.product-spotlight__pack-option--active[data-pack-qty]')
      );
      if (activePack) {
        const packQty = parseInt(activePack.dataset.packQty || '1', 10);
        const discount = this._getBundleDiscountForQty(packQty);
        const perCanCents = Math.round(newBasePrice * (100 - discount) / 100);
        this._singleUnitPriceCents = perCanCents;
        this._singleComparePriceCents = newBasePrice;
        this._singleSaveCents = Math.max(newBasePrice - perCanCents, 0);
        this._singleQty = packQty;
        this.dataset.singleUnitPriceCents = String(perCanCents);
        this._renderPriceRow();
        const addToCartBtn2 = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-add-to-cart]'));
        if (addToCartBtn2 && newVariantId) addToCartBtn2.dataset.variantId = newVariantId;
        const qtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.product-spotlight__qty-input'));
        if (qtyInput) qtyInput.value = String(packQty);
      }
    } else if (Array.isArray(data.packVariants)) {
      // Variant-based mode: rebuild pack buttons for the new flavor.
      // Preserve the pack the user already chose when they switch flavor.
      // Without this, _rebuildPackVariants would default to the new
      // product's `selected_or_first_available_variant` (which is what
      // `data.variantId` holds), so the UI would silently snap back to
      // the first pack on every flavor change.
      //
      // Match by the composite `optionLabel-optionNameUpper` label (e.g.
      // "10-PACK") rather than by variantId — variant IDs differ across
      // flavor products, but the pack-size label is the user intent we
      // want to carry over. Fall back to data.variantId when there's no
      // match (different flavors can expose different pack sizes) or
      // when no prior selection exists (selector was hidden).
      const previousActivePack = /** @type {HTMLElement | null} */ (
        this.querySelector('.product-spotlight__pack-option--active[data-ps-pack-variant]')
      );
      const previousPackLabel = previousActivePack ? (previousActivePack.dataset.pack || '') : '';
      let preservedVariantId = data.variantId;
      if (previousPackLabel) {
        const match = data.packVariants.find((v) => {
          const composite = `${v.optionLabel}-${v.optionNameUpper}`;
          return composite === previousPackLabel;
        });
        if (match && match.variantId) preservedVariantId = match.variantId;
      }
      this._rebuildPackVariants(data.packVariants, preservedVariantId);

      const activePackBtn = /** @type {HTMLElement | null} */ (
        this.querySelector('.product-spotlight__pack-option--active[data-ps-pack-variant]') ||
        this.querySelector('[data-ps-pack-variant]')
      );
      if (activePackBtn && activePackBtn.dataset.url) {
        try {
          const nextUrl = new URL(activePackBtn.dataset.url, window.location.origin);
          nextUrl.hash = window.location.hash;
          const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          const target = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
          if (current !== target) {
            window.history.replaceState(window.history.state, '', target);
          }
        } catch (error) {
          console.warn('[ProductSpotlight] Unable to update URL after flavor change', error);
        }
      }
    }

    // ─── Gallery — Desktop Hero ──────────────────────────────
    const heroImg = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-ps-hero-img]'));
    if (heroImg && data.images && data.images.length > 0) {
      this._swapHeroImage(heroImg, data.images[0], data.title || '', false);
    }

    // ─── Gallery — Desktop Thumbnails ───────────────────────
    const thumbsContainer = this.querySelector('[data-ps-thumbnails]');
    if (thumbsContainer && data.thumbImages && data.thumbImages.length > 0) {
      const thumbImgs = thumbsContainer.querySelectorAll('.product-spotlight__thumb-img');
      data.thumbImages.forEach((url, i) => {
        if (thumbImgs[i]) {
          /** @type {HTMLImageElement} */ (thumbImgs[i]).src = url;
          /** @type {HTMLImageElement} */ (thumbImgs[i]).dataset.psFullSrc = url;
        }
      });
    }

    // ─── Gallery — Mobile Swiper ────────────────────────────
    const swiperWrapper = this.querySelector('[data-ps-swiper-wrapper]');
    if (swiperWrapper && data.mobileImages && data.mobileImages.length > 0) {
      const slides = swiperWrapper.querySelectorAll('.product-spotlight__swiper-img');
      data.mobileImages.forEach((url, i) => {
        if (slides[i]) {
          const slideImg = /** @type {HTMLImageElement} */ (slides[i]);
          this._syncSwiperImageClasses(slideImg, url);
          slideImg.src = url;
        }
      });
      if (this._swiper) {
        this._swiper.slideTo(0, 0);
        this._swiper.update();
      }
    }

    this._singleGalleryState = {
      title: data.title || '',
      images: Array.isArray(data.images) ? data.images : [],
      thumbImages: Array.isArray(data.thumbImages) ? data.thumbImages : [],
      mobileImages: Array.isArray(data.mobileImages) ? data.mobileImages : [],
    };

    if (this._isVarietyMode) {
      this._applyBundleVarietyGallery();
    }

    // ─── Accordions ──────────────────────────────────────────
    this._updateAccordions(Array.isArray(data.accordionItems) ? data.accordionItems : []);
    this._syncQuantityMinimum(parseInt(data.bundleMinQty || '0', 10));

    this._emitSingleState();
  }

  _emitActiveProductChange(data) {
    const title = typeof data?.title === 'string' ? data.title.trim() : '';
    if (!title) return;

    const heroImage = Array.isArray(data?.images) && typeof data.images[0] === 'string'
      ? data.images[0]
      : '';

    document.dispatchEvent(
      new CustomEvent('pdp:active-product-change', {
        detail: {
          source: 'product-spotlight',
          title,
          url: typeof data?.url === 'string' ? data.url : '',
          gridImage: typeof data?.flavorGridImage === 'string' ? data.flavorGridImage : '',
          heroImage,
        },
      })
    );
  }

  _initBundlePricingBridge() {
    this._onBundleStateChange = (event) => {
      const detail = event?.detail || {};
      if (!Number.isInteger(detail.totalCents)) return;
      this._bundleTotalCents = detail.totalCents;
      if (this._isVarietyMode) {
        this._renderPriceRow();
      }
    };

    document.addEventListener('pdp:bundle-state-change', this._onBundleStateChange);
  }

  _renderPriceRow() {
    if (this._isVarietyMode) {
      const bundleTotalCents = Math.max(this._bundleTotalCents, 0);
      const bundlePrice = this._formatMoney(bundleTotalCents);
      this.querySelectorAll('[data-ps-price]').forEach((el) => {
        el.textContent = bundlePrice;
      });

      this.querySelectorAll('[data-ps-compare-price]').forEach((el) => {
        /** @type {HTMLElement} */ (el).style.display = 'none';
      });
      this.querySelectorAll('[data-ps-save-badge]').forEach((el) => {
        /** @type {HTMLElement} */ (el).style.display = 'none';
      });
      return;
    }

    const qty = this._singleQty > 0 ? this._singleQty : 1;
    const priceCents = this._singleUnitPriceCents * qty;
    if (priceCents > 0) {
      const singlePrice = this._formatMoney(priceCents);
      this.querySelectorAll('[data-ps-price]').forEach((el) => {
        el.textContent = singlePrice;
      });
      this.querySelectorAll('[data-ps-purchase-price]').forEach((el) => {
        el.textContent = singlePrice;
      });
    }

    this.querySelectorAll('[data-ps-compare-price]').forEach((el) => {
      const compareEl = /** @type {HTMLElement} */ (el);
      const hasCompare = this._singleComparePriceCents > this._singleUnitPriceCents;
      if (!hasCompare) {
        compareEl.style.display = 'none';
        return;
      }

      compareEl.textContent = this._formatMoney(this._singleComparePriceCents * qty);
      compareEl.style.display = '';
    });

    this.querySelectorAll('[data-ps-save-badge]').forEach((el) => {
      const saveEl = /** @type {HTMLElement} */ (el);
      if (this._singleSaveCents > 0) {
        saveEl.textContent = `Save ${this._formatMoney(this._singleSaveCents * qty)}`;
        saveEl.style.display = '';
        return;
      }

      saveEl.style.display = 'none';
    });

    this._syncSubscribePricingFromSelectedPlan();
  }

  _readMoneyFromElement(selector) {
    const el = this.querySelector(selector);
    if (!(el instanceof HTMLElement)) return 0;
    const text = String(el.textContent || '').replace(/[^\d.,-]/g, '');
    if (!text) return 0;

    const lastComma = text.lastIndexOf(',');
    const lastDot = text.lastIndexOf('.');
    const sep = Math.max(lastComma, lastDot);

    if (sep === -1) {
      const intOnly = parseInt(text.replace(/[^\d-]/g, ''), 10);
      return Number.isNaN(intOnly) ? 0 : intOnly * 100;
    }

    const intPart = text.slice(0, sep).replace(/[^\d-]/g, '');
    const decPart = text.slice(sep + 1).replace(/[^\d]/g, '').slice(0, 2).padEnd(2, '0');
    const cents = parseInt(`${intPart}${decPart}`, 10);
    return Number.isNaN(cents) ? 0 : cents;
  }

  _emitPackModeState() {
    this._renderPriceRow();

    const activePack = /** @type {HTMLElement | null} */ (
      this.querySelector('.product-spotlight__pack-option.product-spotlight__pack-option--active')
    );
    const isVariety = Boolean(activePack && (activePack.dataset.pack || '').includes('VARIETY'));

    document.dispatchEvent(
      new CustomEvent('pdp:pack-mode-change', {
        detail: {
          source: 'product-spotlight',
          varietyActive: isVariety,

        },
      })
    );
  }

  _emitSingleState() {
    this._renderPriceRow();

    document.dispatchEvent(
      new CustomEvent('pdp:single-state-change', {
        detail: {
          source: 'product-spotlight',
          quantity: this._singleQty,
          unitPriceCents: this._singleUnitPriceCents,
        },
      })
    );
  }

  _updateSubscribeOptions(data) {
    const subscribeOption = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-subscribe-option]'));
    const subscribeRadio = /** @type {HTMLInputElement | null} */ (
      subscribeOption ? subscribeOption.querySelector('input.product-spotlight__radio[value="subscribe"]') : null
    );
    const oneTimeRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input.product-spotlight__radio[value="one-time"]')
    );
    const deliveryLabel = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-delivery-text]'));
    const deliveryList = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-delivery-options]'));
    const deliveryTrigger = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.product-spotlight__delivery-row')
    );

    if (!subscribeOption || !subscribeRadio || !deliveryLabel || !deliveryList || !deliveryTrigger) return;

    const sellingPlans = Array.isArray(data.sellingPlans) ? data.sellingPlans : [];
    const hasSellingPlan = sellingPlans.length > 0;

    subscribeOption.style.display = hasSellingPlan ? '' : 'none';
    subscribeRadio.disabled = !hasSellingPlan;

    if (!hasSellingPlan) {
      if (subscribeRadio.checked && oneTimeRadio) {
        oneTimeRadio.checked = true;
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      deliveryList.innerHTML = '';
      deliveryLabel.textContent = '';
      delete deliveryTrigger.dataset.psSelectedSellingPlanId;
      this._setSubscribePricing(null);
      return;
    }

    deliveryList.innerHTML = '';
    const fragment = document.createDocumentFragment();
    sellingPlans.forEach((plan, index) => {
      const option = document.createElement('li');
      option.className = 'product-spotlight__delivery-option';
      if (index === 0) option.classList.add('product-spotlight__delivery-option--selected');
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      option.dataset.sellingPlanId = String(plan.id);
      option.dataset.sellingPlanPrice = String(plan.price);
      option.dataset.sellingPlanComparePrice = String(plan.comparePrice);
      option.dataset.sellingPlanDiscountPct = String(plan.discountPct);
      option.textContent = plan.name || '';
      fragment.appendChild(option);
    });
    deliveryList.appendChild(fragment);
    deliveryLabel.textContent = sellingPlans[0].name || '';
    deliveryTrigger.dataset.psSelectedSellingPlanId = String(sellingPlans[0].id);
    this._setSubscribePricing(sellingPlans[0]);
  }

  /**
   * Update variety bundle card data based on selected flavor metafield.
   * @param {Object} data
   */
  _updateBundleBuilder(data) {
    const bundleBuilder = this.querySelector('bundle-builder');
    if (!bundleBuilder || typeof bundleBuilder.updateVariety !== 'function') return;

    bundleBuilder.updateVariety({
      bundleSize: data.bundleSize,
      bundleMaxQty: data.bundleMaxQty,
      bundleMinQty: data.bundleMinQty,
      bundleSellingPlans: Array.isArray(data.bundleSellingPlans) ? data.bundleSellingPlans : [],
      products: Array.isArray(data.bundleProducts) ? data.bundleProducts : [],
    });
  }

  _shouldStartInVarietyFromURL() {
    if (this.dataset.bundleBuilderOnly === 'true') return true;

    const params = new URLSearchParams(window.location.search);
    const pack = (params.get('pack') || '').toLowerCase();
    if (pack === 'variety' || pack === 'bundle-variety' || pack === 'bundle_variety') return true;

    const bundle = (params.get('bundle') || '').toLowerCase();
    if (bundle === '1' || bundle === 'true' || bundle === 'yes' || bundle === 'variety') return true;

    return false;
  }

  _resolveBundleVarietyImages() {
    const indexed = [];

    Array.from(this.attributes).forEach((attr) => {
      const match = attr.name.match(/^data-bundle-variety-image-(\d+)$/);
      if (!match) return;

      const index = parseInt(match[1], 10);
      const value = typeof attr.value === 'string' ? attr.value.trim() : '';
      if (!Number.isInteger(index) || index <= 0 || !value) return;

      indexed.push({ index, value });
    });

    indexed.sort((a, b) => a.index - b.index);
    return indexed.map((entry) => entry.value);
  }

  _readInitialGalleryState() {
    const heroImg = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-ps-hero-img]'));
    const thumbImgs = Array.from(this.querySelectorAll('.product-spotlight__thumb-img'));
    const mobileImgs = Array.from(this.querySelectorAll('.product-spotlight__swiper-img'));

    const heroSrc = heroImg?.src || '';
    const thumbSrcs = thumbImgs
      .map((img) => img instanceof HTMLImageElement ? img.src : '')
      .filter(Boolean);

    return {
      title: (heroImg?.alt || '').trim(),
      images: heroSrc ? [heroSrc, ...thumbSrcs] : thumbSrcs,
      thumbImages: thumbSrcs,
      mobileImages: mobileImgs.map((img) => img instanceof HTMLImageElement ? img.src : '').filter(Boolean),
    };
  }

  _applyBundleVarietyGallery(animate = false) {
    const cachedUrls = Array.isArray(this._bundleVarietyImages) ? this._bundleVarietyImages : [];
    const schemaUrls = this._resolveBundleVarietyImages();
    const urls = cachedUrls.length > 0 ? cachedUrls : schemaUrls;

    if (schemaUrls.length > 0 && urls.length === 0) {
      this._bundleVarietyImages = schemaUrls;
    } else if (urls.length > 0) {
      this._bundleVarietyImages = urls;
    }

    if (!Array.isArray(urls) || urls.length === 0) {
      this._applySingleGalleryState();
      return;
    }

    const fallback = urls[0];
    const desktopThumbUrls = urls.slice(1, 5);

    const heroImg = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-ps-hero-img]'));
    if (heroImg && fallback) {
      this._swapHeroImage(heroImg, fallback, 'Bundle Variety', animate);
    }

    const thumbImgs = this._ensureThumbSlots(desktopThumbUrls.length);
    thumbImgs.forEach((img, index) => {
      if (!(img instanceof HTMLImageElement)) return;
      const nextSrc = desktopThumbUrls[index] || fallback;
      img.src = nextSrc;
      img.alt = 'Bundle Variety';
      img.dataset.psFullSrc = nextSrc;
    });

    const slides = this._ensureMobileSlideSlots(urls.length);
    slides.forEach((img, index) => {
      if (!(img instanceof HTMLImageElement)) return;
      const nextSrc = urls[index] || fallback;
      this._syncSwiperImageClasses(img, nextSrc);
      img.src = nextSrc;
      img.alt = 'Bundle Variety';
    });

    if (this._swiper) {
      this._swiper.slideTo(0, 0);
      this._swiper.update();
    }
  }

  _applySingleGalleryState(animate = false) {
    const state = this._singleGalleryState;
    if (!state) return;

    const heroUrl = Array.isArray(state.images) && state.images.length > 0 ? state.images[0] : '';
    const heroImg = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-ps-hero-img]'));
    if (heroImg && heroUrl) {
      this._swapHeroImage(heroImg, heroUrl, state.title || '', animate);
    }

    const singleThumbUrls = Array.isArray(state.thumbImages) ? state.thumbImages.slice(0, 4) : [];
    const thumbImgs = this._ensureThumbSlots(singleThumbUrls.length);
    thumbImgs.forEach((img, index) => {
      if (!(img instanceof HTMLImageElement)) return;
      const nextSrc = singleThumbUrls[index] || '';
      if (nextSrc) {
        img.src = nextSrc;
        img.alt = state.title || '';
        img.dataset.psFullSrc = nextSrc;
      }
    });

    const singleMobileUrls = Array.isArray(state.mobileImages) ? state.mobileImages : [];
    const slides = this._ensureMobileSlideSlots(singleMobileUrls.length);
    slides.forEach((img, index) => {
      if (!(img instanceof HTMLImageElement)) return;
      const nextSrc = singleMobileUrls[index] || '';
      if (nextSrc) {
        this._syncSwiperImageClasses(img, nextSrc);
        img.src = nextSrc;
        img.alt = state.title || '';
      }
    });

    if (this._swiper) {
      this._swiper.slideTo(0, 0);
      this._swiper.update();
    }
  }

  _ensureThumbSlots(count) {
    const container = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-thumbnails]'));
    if (!container) return [];

    const target = Math.max(0, Math.min(count, 4));
    let thumbs = Array.from(container.querySelectorAll('.product-spotlight__thumb'));

    while (thumbs.length < target) {
      const thumb = document.createElement('div');
      thumb.className = 'product-spotlight__thumb';

      const img = document.createElement('img');
      img.className = 'product-spotlight__thumb-img';
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      img.alt = '';
      img.dataset.psFullSrc = '';

      thumb.appendChild(img);
      container.appendChild(thumb);
      thumbs.push(thumb);
    }

    while (thumbs.length > target) {
      const thumb = thumbs.pop();
      if (thumb) thumb.remove();
    }

    return Array.from(container.querySelectorAll('.product-spotlight__thumb-img'));
  }

  _swapHeroImage(heroImg, src, alt, animate = false) {
    if (!(heroImg instanceof HTMLImageElement) || !src) return;

    this._syncHeroImageClasses(heroImg, src);

    const currentSrc = heroImg.currentSrc || heroImg.src;
    if (currentSrc === src) {
      heroImg.alt = alt;
      return;
    }

    this._heroSwapRequestId = (this._heroSwapRequestId || 0) + 1;
    const requestId = this._heroSwapRequestId;

    const applySwap = () => {
      if (this._heroSwapRequestId !== requestId) return;

      this._syncHeroImageClasses(heroImg, src);
      heroImg.src = src;
      heroImg.alt = alt;
    };

    const preload = new Image();
    preload.decoding = 'async';
    preload.src = src;

    if (preload.complete) {
      applySwap();
      return;
    }

    preload.onload = applySwap;
    preload.onerror = () => {
      if (this._heroSwapRequestId !== requestId) return;
      // Fallback: swap anyway so the gallery never gets stuck waiting on preload.
      applySwap();
    };
  }

  _isIdentifierImage(src) {
    return typeof src === 'string' && src.toLowerCase().includes('identifier-image');
  }

  _syncHeroImageClasses(heroImg, src) {
    if (!(heroImg instanceof HTMLImageElement)) return;

    // Keep base class even if another script mutates className while swapping images.
    heroImg.classList.add('product-spotlight__hero-img');
    heroImg.classList.toggle('product-spotlight__hero-img--identifier', this._isIdentifierImage(src));
  }

  _syncSwiperImageClasses(swiperImg, src) {
    if (!(swiperImg instanceof HTMLImageElement)) return;

    swiperImg.classList.add('product-spotlight__swiper-img');
    swiperImg.classList.toggle('product-spotlight__swiper-img--identifier', this._isIdentifierImage(src));
  }

  _ensureMobileSlideSlots(count) {
    const wrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-ps-swiper-wrapper]'));
    if (!wrapper) return [];

    const target = Math.max(0, count);
    let slides = Array.from(wrapper.querySelectorAll('.product-spotlight__swiper-slide'));

    while (slides.length < target) {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide product-spotlight__swiper-slide';

      const img = document.createElement('img');
      img.className = 'product-spotlight__swiper-img';
      img.loading = 'lazy';
      img.width = 600;
      img.height = 600;
      img.alt = '';

      slide.appendChild(img);
      wrapper.appendChild(slide);
      slides.push(slide);
    }

    while (slides.length > target) {
      const slide = slides.pop();
      if (slide) slide.remove();
    }

    return Array.from(wrapper.querySelectorAll('.product-spotlight__swiper-img'));
  }

  /**
   * Update accordion titles + content from flavor data
   * @param {Array<{title:string, content:string}>} items
   */
  _updateAccordions(items) {
    const container = this.querySelector('[data-ps-accordions]');
    if (!container) return;

    const itemEls = container.querySelectorAll('[data-ps-accordion-item]');

    items.forEach((item, i) => {
      if (itemEls[i]) {
        const titleEl = itemEls[i].querySelector('[data-ps-accordion-title]');
        const contentEl = itemEls[i].querySelector('[data-ps-accordion-content]');
        if (titleEl) titleEl.textContent = item.title || '';
        if (contentEl) contentEl.innerHTML = item.content || '';
        /** @type {HTMLElement} */ (itemEls[i]).style.display = '';
      }
    });

    // Hide surplus accordion items if new flavor has fewer
    for (let i = items.length; i < itemEls.length; i++) {
      /** @type {HTMLElement} */ (itemEls[i]).style.display = 'none';
    }
  }

  /* ── Add to Cart (single pack) ────────────────────────────── */
  _initAddToCart() {
    const btn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ps-add-to-cart]'));
    if (!btn) return;

    btn.addEventListener('click', async () => {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const qtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.product-spotlight__qty-input'));
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      const subscribeRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector('input.product-spotlight__radio[value="subscribe"]')
      );
      const deliveryTrigger = /** @type {HTMLButtonElement | null} */ (
        this.querySelector('.product-spotlight__delivery-row')
      );

      const selectedSellingPlanId =
        subscribeRadio && subscribeRadio.checked && deliveryTrigger
          ? parseInt(deliveryTrigger.dataset.psSelectedSellingPlanId || '0', 10)
          : 0;

      const lineItemPayload = { id: parseInt(variantId, 10), quantity: qty };
        const minQty = parseInt(this.querySelector('.product-spotlight__qty-input')?.min || '1', 10);
        if (Number.isInteger(minQty) && minQty > 1) {
          lineItemPayload.properties = {
            _bb_bundle_min_qty: String(minQty),
          };
        }
      if (selectedSellingPlanId > 0) {
        lineItemPayload.selling_plan = selectedSellingPlanId;
      }

      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'ADDING…';

      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [lineItemPayload] }),
        });

        if (!addRes.ok) {
          const err = await addRes.json().catch(() => ({}));
          throw new Error(err.description || 'Cart error');
        }

        const cartItemsEl = /** @type {HTMLElement | null} */ (
          document.querySelector('cart-items-component[data-section-id]')
        );
        const sectionId = cartItemsEl ? cartItemsEl.dataset.sectionId : null;
        const cartUrl = sectionId ? `/cart?sections=${sectionId}` : '/cart.js';
        const cartData = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then(r => r.json());

        if (sectionId && cartData[sectionId] && cartItemsEl) {
          const parser = new DOMParser();
          const freshDoc = parser.parseFromString(cartData[sectionId], 'text/html');
          const freshEl = freshDoc.querySelector('cart-items-component');
          if (freshEl) cartItemsEl.innerHTML = freshEl.innerHTML;
        }

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cartData, data: { itemCount: cartData.item_count } },
          })
        );

        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();

        btn.textContent = '✓ ADDED TO CART!';
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 2000);
      } catch (err) {
        console.error('[ProductSpotlight] Cart error', err);
        btn.disabled = false;
        btn.textContent = originalText;
      }
    });
  }
}

if (!customElements.get('product-spotlight')) {
  customElements.define('product-spotlight', ProductSpotlight);
}